import { defineStore, storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { computed, ref, watch, nextTick } from 'vue'
import { useOverlaysStore } from '@/stores/overlays.js'
import { useNavBarStore } from '@shared/stores/navBar.js'

export const useScrollStore = defineStore('scroll', () => {
  const scrollPosition = ref(0)
  const scrollbarWidth = ref(0)
  const recentlyNavigatedToAnchor = ref(false)
  const { isExpanded } = storeToRefs(useNavBarStore())
  const { isAnyOverlayOpen, drawerIsDragging } = storeToRefs(useOverlaysStore())
  const isScrollLocked = computed(() => {
    return isExpanded.value || isAnyOverlayOpen.value || drawerIsDragging.value
  })

  function updateScrollPosition() {
    scrollPosition.value = window.scrollY
  }

  function calculateScrollbarWidth() {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll'
    outer.style.width = '100px'
    outer.style.position = 'absolute'
    outer.style.top = '-9999px'
    document.body.appendChild(outer)

    const inner = document.createElement('div')
    inner.style.width = '100%'
    outer.appendChild(inner)

    scrollbarWidth.value = outer.offsetWidth - inner.offsetWidth

    outer.remove()
  }
  function lockScroll() {
    updateScrollPosition()
    calculateScrollbarWidth()
  }
  function unlockScroll() {
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPosition.value, behavior: 'instant' })

      if (recentlyNavigatedToAnchor.value) {
        setTimeout(() => {
          handleAnchorScroll()
          recentlyNavigatedToAnchor.value = false
        }, 50)
      }
    })
  }

  watch(isScrollLocked, (locked) => {
    if (locked) {
      lockScroll()
    } else {
      unlockScroll()
    }
  })
  
  const route = useRoute()

  function handleAnchorScroll() {
    const hash = window.location.hash
    if (hash) {
      const anchor = document.querySelector(hash)
      if (anchor) {
        const top = parseInt(getComputedStyle(anchor).scrollMarginTop)
        const y = anchor.getBoundingClientRect().top + window.scrollY - (isNaN(top) ? 0 : top)
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }
  }
  watch(
    () => route.hash,
    async (newHash, oldHash) => {
      if (newHash && newHash !== oldHash && newHash.startsWith('#')) {
        recentlyNavigatedToAnchor.value = true
        await nextTick()
        setTimeout(() => {
          recentlyNavigatedToAnchor.value = false
        }, 2000)
      }
    }
  )
  
  return {
    scrollPosition,
    isScrollLocked,
    scrollbarWidth,
    updateScrollPosition,
    calculateScrollbarWidth,
    lockScroll,
    unlockScroll
  }
})
